import React from "react";

import { useTranslation } from "lib/i18n/use-translation";

import Info from "../Info";
import styles from "./business-impact.module.scss";
import {
  formatCompactMoney,
  formatMoney,
  formatMonth,
  formatMultiple,
  formatPercent,
} from "./business-impact-view";
import ProgressBlock from "./ProgressBlock";
import { RoiSample, ValueDriverId } from "./sample";
import ValueDriverRing, { DRIVER_COLOR, METER_COLOR } from "./ValueDriverRing";

const K = "admin:companyDashboard.businessImpact";

// The dark summary card and the value drivers under it, for a client that has
// an ROI figure to show.
const RoiSummary = ({ sample }: { sample: RoiSample }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const locale = i18n.language;
  const { roiMultiple, totalSavings, valueDrivers } = sample;
  const { currency } = totalSavings;
  const eyebrow =
    sample.version === "projected"
      ? t(`${K}.summary.projectedEyebrow`, { months: sample.basis.months })
      : t(`${K}.summary.roiEyebrow`);
  const driverName = (id: ValueDriverId) => t(`${K}.drivers.names.${id}`);
  const money = (amount: number) => formatMoney(amount, currency, locale);
  const ringLabel = t(`${K}.summary.chartLabel`, {
    total: money(totalSavings.amount),
    drivers: valueDrivers
      .map((driver) =>
        t(`${K}.summary.chartDriver`, {
          name: driverName(driver.id),
          amount: money(driver.amount),
          share: formatPercent(driver.sharePercent, locale),
        })
      )
      .join("; "),
  });

  return (
    <>
      <section className={styles["summary"]}>
        <div className={styles["summary-roi"]}>
          <div className={styles["eyebrow"]}>
            <h3 className={styles["eyebrow-heading"]}>{eyebrow}</h3>
            <Info
              figure={eyebrow}
              text={t(`${K}.summary.roiInfo`)}
              align="start"
            />
            {sample.version === "projected" && (
              <span className={styles["projected-pill"]}>
                {t(`${K}.projected`)}
              </span>
            )}
          </div>
          <p className={styles["multiple"]}>
            {formatMultiple(roiMultiple, locale)}
            <span className={styles["multiple-x"]}>
              {t(`${K}.summary.multipleSign`)}
            </span>
            {sample.version === "projected" && (
              <span className={styles["multiple-qualifier"]}>
                {t(`${K}.projected`)}
              </span>
            )}
          </p>
          <p className={styles["eyebrow"]}>
            {t(
              sample.version === "projected"
                ? `${K}.summary.savingsToDate`
                : `${K}.summary.savings`
            )}
          </p>
          <p className={styles["savings"]}>{money(totalSavings.amount)}</p>
          {sample.version === "projected" ? (
            <>
              <p className={styles["basis-quote"]}>
                {t(`${K}.summary.basisProjected`, {
                  months: sample.basis.months,
                  sector: t(`${K}.summary.sectors.${sample.basis.sector}`),
                })}
              </p>
              <ProgressBlock
                label={t(`${K}.summary.progressLabel`)}
                month={sample.progress.month}
                of={sample.progress.of}
                valueText={t(`${K}.summary.progressMonth`, sample.progress)}
                note={t(`${K}.summary.progressNote`, {
                  of: sample.progress.of,
                  review: formatMonth(sample.progress.review, locale),
                })}
              />
            </>
          ) : (
            <p className={styles["basis"]}>
              {t(`${K}.summary.basis`, {
                from: formatMonth(sample.period.from, locale),
                to: formatMonth(sample.period.to, locale),
                next: t(`${K}.summary.quarter`, sample.period.nextCalculation),
              })}
            </p>
          )}
        </div>
        <div className={styles["summary-drivers"]}>
          <p className={styles["eyebrow"]}>
            {t(`${K}.summary.driversEyebrow`)}
          </p>
          <ValueDriverRing
            drivers={valueDrivers}
            label={ringLabel}
            totalLabel={t(`${K}.summary.chartTotal`)}
            total={formatCompactMoney(totalSavings.amount, currency, locale)}
          />
          <ul
            className={styles["legend"]}
            aria-label={t(`${K}.summary.driversEyebrow`)}
          >
            {valueDrivers.map((driver) => (
              <li key={driver.id}>
                <span
                  className={styles["dot"]}
                  style={{ backgroundColor: DRIVER_COLOR[driver.id] }}
                  aria-hidden="true"
                />
                {driverName(driver.id)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={styles["drivers"]}>
        <h3 className={styles["section-heading"]}>{t(`${K}.drivers.title`)}</h3>
        <p className={styles["subheading"]}>
          {t(
            sample.version === "projected"
              ? `${K}.drivers.subtitleProjected`
              : `${K}.drivers.subtitle`
          )}
        </p>
        <ul className={styles["driver-cards"]}>
          {valueDrivers.map((driver) => {
            const id = driver.id;
            return (
              <li key={id} className={styles["driver-card"]}>
                <div className={styles["driver-head"]}>
                  <h4 className={styles["driver-name"]}>{driverName(id)}</h4>
                  {sample.version === "projected" && (
                    <span className={styles["chip"]}>
                      {t(`${K}.projected`)}
                    </span>
                  )}
                </div>
                <p className={styles["driver-amount"]}>
                  {money(driver.amount)}
                </p>
                <div className={styles["share"]}>
                  <span>{t(`${K}.drivers.shareOfTotal`)}</span>
                  <strong>{formatPercent(driver.sharePercent, locale)}</strong>
                </div>
                <div className={styles["meter"]} aria-hidden="true">
                  <span
                    style={{
                      width: `${driver.sharePercent}%`,
                      backgroundColor: METER_COLOR[id],
                    }}
                  />
                </div>
                <p className={styles["driver-explanation"]}>
                  {t(`${K}.drivers.explanations.${id}`)}
                </p>
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};

export default RoiSummary;
